/**
 * DesktopPet — gravity-based side-scroller with full squash & stretch.
 * Walks on the taskbar AND on top of any open window.
 * Sits above windows visually (z-index 9995) so it's always visible.
 */
import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
} from 'react';

/* ── sprites (left / right / front + squish twins) ── */
import spriteRight from '../assets/pet/right.png';
import spriteLeft  from '../assets/pet/left.png';
import spriteFront from '../assets/pet/front.png';
import squishRight from '../assets/pet/squish/right.png';
import squishLeft  from '../assets/pet/squish/left.png';
import squishFront from '../assets/pet/squish/front.png';

/* ── physics / size constants ── */
const isMobileDevice = window.innerWidth < 640;
const PET_W    = isMobileDevice ? 72 : 128;   // px — sprite render width
const PET_H    = isMobileDevice ? 72 : 128;   // px — sprite render height
const TASKBAR  = 68;    // px — taskbar height (pet stands above this)
const SPEED    = 1.8;   // px / frame while walking
const GRAVITY  = 0.58;  // px / frame² acceleration
const JUMP_VY  = -16;   // initial jump velocity (negative = up)

/* squash / stretch limits */
const STRETCH_MAX = 1.65;
const SQUASH_MIN  = 1 / STRETCH_MAX;

type Mode    = 'idle' | 'walk-left' | 'walk-right' | 'airborne' | 'dragging';
type FaceDir = 'left' | 'right' | 'front';

/* ── surface helpers ──────────────────────────────────────────────── */

interface Surface { left: number; right: number; surfaceY: number; }

/** Collect all standing surfaces: taskbar + non-minimised windows. */
function getSurfaces(): Surface[] {
  const out: Surface[] = [
    // Taskbar — full width, top edge = window bottom − taskbar height
    { left: 0, right: window.innerWidth, surfaceY: window.innerHeight - TASKBAR - PET_H },
  ];
  document.querySelectorAll<HTMLElement>('[data-window-surface="true"]:not([data-minimized="true"])')
    .forEach(el => {
      const r = el.getBoundingClientRect();
      if (r.width > 20 && r.height > 20) {
        out.push({ left: r.left, right: r.right, surfaceY: r.top - PET_H });
      }
    });
  return out;
}

/**
 * Highest surface the pet's centre passes over while falling from fromY→toY.
 * Returns the pet-top Y to land at, or null if nothing hit.
 */
function findLandY(petX: number, fromY: number, toY: number): number | null {
  const cx = petX + PET_W / 2;
  let best: number | null = null;
  for (const s of getSurfaces()) {
    if (cx < s.left || cx > s.right) continue;
    // Did we cross this surface going downward?
    if (fromY < s.surfaceY && toY >= s.surfaceY) {
      if (best === null || s.surfaceY < best) best = s.surfaceY;
    }
  }
  return best;
}

/**
 * Lowest surface directly below the pet's centre at petX (= ground to snap to).
 * Only includes surfaces that are at-or-below the pet's current top Y.
 */
function getGroundY(petX: number, currentY: number): number | null {
  const cx = petX + PET_W / 2;
  let best: number | null = null;
  for (const s of getSurfaces()) {
    if (cx < s.left || cx > s.right) continue;
    if (s.surfaceY < currentY - 6) continue; // above pet — skip
    if (best === null || s.surfaceY < best) best = s.surfaceY;
  }
  return best;
}

/* ─────────────────────────────────────────────────────────────────── */

const DesktopPet: React.FC<{ isDarkMode?: boolean }> = ({ isDarkMode = false }) => {
  const [sprite,    setSprite]    = useState(spriteFront);
  const [scaleX,    setScaleX]    = useState(1);
  const [scaleY,    setScaleY]    = useState(1);
  const [isWalking, setIsWalking] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  /* mutable runtime state */
  const pos       = useRef({ x: window.innerWidth / 2, y: window.innerHeight - TASKBAR - PET_H });
  const vy        = useRef(0);
  const vx        = useRef(0);          // horizontal throw velocity
  const swayAngle = useRef(0);          // current body tilt in degrees
  const mode      = useRef<Mode>('idle');
  const face      = useRef<FaceDir>('front');
  const el        = useRef<HTMLDivElement>(null);
  const rafId     = useRef(0);
  const timer     = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* drag tracking */
  const dragging  = useRef(false);
  const anchor    = useRef({ mx: 0, my: 0, px: 0, py: 0 });
  const lastMouse = useRef({ x: 0, y: 0 });
  const dragVel   = useRef({ x: 0, y: 0 });   // smoothed mouse velocity for throw

  /* ── position writer (bypasses React state for performance) ── */
  const applyPos = useCallback((x: number, y: number) => {
    const maxX = window.innerWidth  - PET_W;
    const maxY = window.innerHeight - TASKBAR - PET_H;
    const cx = Math.max(0, Math.min(maxX, x));
    const cy = Math.max(0, Math.min(maxY, y));
    pos.current = { x: cx, y: cy };
    if (el.current) {
      el.current.style.transform = `translate(${cx}px, ${cy}px) rotate(${swayAngle.current}deg)`;
    }
  }, []);

  /* ── resize guard — clamp pet back inside viewport when browser shrinks ── */
  useEffect(() => {
    const onResize = () => {
      const { x, y } = pos.current;
      applyPos(x, y);                  // applyPos re-clamps to new dimensions
      // If airborne and now below new ground, snap to ground
      const gY = window.innerHeight - TASKBAR - PET_H;
      if (pos.current.y >= gY && mode.current === 'airborne') {
        mode.current = 'idle';
        face.current = 'front';
        setSprite(spriteFront);
        setIsWalking(false);
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [applyPos]);

  /* Sync position after every React render so React can't overwrite it */
  useLayoutEffect(() => {
    if (el.current) {
      const { x, y } = pos.current;
      el.current.style.transform = `translate(${x}px, ${y}px) rotate(${swayAngle.current}deg)`;
    }
  });

  /* ── squash/stretch helpers ── */
  const applyStretch = useCallback((vx: number, vy: number, transition = 0.08) => {
    const speed   = Math.hypot(vx, vy);
    const stretch = Math.min(1 + speed * 0.06, STRETCH_MAX);
    const squash  = Math.max(1 / stretch, SQUASH_MIN);
    if (Math.abs(vx) >= Math.abs(vy)) {
      setScaleX(stretch); setScaleY(squash);   // horizontal stretch
    } else {
      setScaleX(squash);  setScaleY(stretch);  // vertical stretch
    }
    return transition;
  }, []);

  const resetScale = useCallback((springy = false) => {
    setScaleX(1); setScaleY(1);
    return springy;
  }, []);

  /* landing squash — wide + flat, then spring back */
  const landSquash = useCallback(() => {
    setScaleX(1.5); setScaleY(0.6);
    setTimeout(() => { setScaleX(0.85); setScaleY(1.1); }, 90);
    setTimeout(() => resetScale(true), 200);
  }, [resetScale]);

  /* ── action scheduler ── */
  const clearTimer = () => { if (timer.current) clearTimeout(timer.current); };

  const scheduleAction = useCallback(() => {
    clearTimer();
    const delay = 700 + Math.random() * 2800;
    timer.current = setTimeout(() => {
      if (dragging.current || mode.current === 'airborne') return;
      pickAction();
    }, delay);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const pickAction = useCallback(() => {
    const roll = Math.random();
    if (roll < 0.35) {
      mode.current = 'walk-right'; face.current = 'right';
      setSprite(spriteRight); setIsWalking(true);
    } else if (roll < 0.70) {
      mode.current = 'walk-left'; face.current = 'left';
      setSprite(spriteLeft); setIsWalking(true);
    } else if (roll < 0.84) {
      vy.current = JUMP_VY;
      mode.current = 'airborne';
      setIsWalking(false);
      /* stretch upward on jump */
      setScaleX(0.75); setScaleY(1.4);
      setTimeout(() => resetScale(), 180);
    } else {
      mode.current = 'idle'; face.current = 'front';
      setSprite(spriteFront); setIsWalking(false);
    }
    scheduleAction();
  }, [scheduleAction, resetScale]);

  /* ── RAF physics + walk loop ── */
  useEffect(() => {
    const loop = () => {
      if (!dragging.current) {
        const { x, y } = pos.current;

        if (mode.current === 'airborne') {
          vy.current += GRAVITY;
          vx.current *= 0.984;   // horizontal air friction

          /* lean in flight direction — tilts with the arc */
          const targetSway = Math.max(-35, Math.min(35, vx.current * 2.2));
          swayAngle.current = swayAngle.current * 0.88 + targetSway * 0.12;

          const rawX = x + vx.current;
          const newY = y + vy.current;

          /* wall bounce — lose 40 % of horizontal speed */
          const maxX = window.innerWidth - PET_W;
          let  bouncedX = rawX;
          if (rawX <= 0) { vx.current = Math.abs(vx.current) * 0.6; bouncedX = 0; }
          else if (rawX >= maxX) { vx.current = -Math.abs(vx.current) * 0.6; bouncedX = maxX; }

          const hit = findLandY(bouncedX, y, newY);

          if (hit !== null) {
            vy.current        = 0;
            vx.current       *= 0.4;   // skid friction on landing
            swayAngle.current = 0;
            mode.current      = 'idle';
            face.current      = 'front';
            setSprite(spriteFront); setIsWalking(false);
            applyPos(bouncedX, hit);
            landSquash();
            scheduleAction();
          } else {
            applyPos(bouncedX, newY);
          }

        } else if (mode.current === 'walk-right' || mode.current === 'walk-left') {
          /* decay sway while walking */
          swayAngle.current *= 0.88;

          const dir = mode.current === 'walk-right' ? 1 : -1;
          const nx  = Math.max(0, Math.min(window.innerWidth - PET_W, x + SPEED * dir));
          const gY  = getGroundY(nx, y);

          if (gY === null || gY > y + 12) {
            mode.current = 'airborne'; vy.current = 1; setIsWalking(false);
            applyPos(nx, y);
          } else {
            applyPos(nx, gY);
            if (nx <= 0 && dir < 0) {
              mode.current = 'walk-right'; face.current = 'right'; setSprite(spriteRight);
            } else if (nx >= window.innerWidth - PET_W && dir > 0) {
              mode.current = 'walk-left'; face.current = 'left'; setSprite(spriteLeft);
            }
          }

        } else if (mode.current === 'idle') {
          /* decay sway + apply any residual vx skid */
          swayAngle.current *= 0.85;
          vx.current        *= 0.80;
          const skidX = Math.abs(vx.current) > 0.3 ? x + vx.current : x;

          /* keep pet snapped to surface if window moves */
          const gY = getGroundY(skidX, y);
          if (gY !== null && gY > y + 8) {
            mode.current = 'airborne'; vy.current = 1;
          } else if (gY !== null && (Math.abs(gY - y) > 2 || Math.abs(vx.current) > 0.3)) {
            applyPos(skidX, gY);
          } else if (Math.abs(swayAngle.current) > 0.05) {
            /* re-write transform even when position is stable so sway decays visually */
            if (el.current) {
              el.current.style.transform = `translate(${x}px, ${y}px) rotate(${swayAngle.current}deg)`;
            }
          }
        }
      }
      rafId.current = requestAnimationFrame(loop);
    };

    applyPos(pos.current.x, pos.current.y);
    rafId.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafId.current);
  }, [applyPos, scheduleAction, landSquash]);

  useEffect(() => {
    scheduleAction();
    return clearTimer;
  }, [scheduleAction]);

  /* ── drag handler ── */
  /* ── shared drag start logic (called by both mouse and touch) ── */
  const startDrag = useCallback((clientX: number, clientY: number) => {
    dragging.current    = true;
    mode.current        = 'dragging';
    vy.current          = 0;
    vx.current          = 0;
    swayAngle.current   = 0;
    dragVel.current     = { x: 0, y: 0 };
    setIsWalking(false);
    setIsDragging(true);
    clearTimer();

    anchor.current    = { mx: clientX, my: clientY, px: pos.current.x, py: pos.current.y };
    lastMouse.current = { x: clientX, y: clientY };

    const sq = face.current === 'right' ? squishRight
             : face.current === 'left'  ? squishLeft
             : squishFront;
    setSprite(sq);
    setScaleX(1.35); setScaleY(0.7);
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const moveDrag = useCallback((clientX: number, clientY: number) => {
    const dx = clientX - lastMouse.current.x;
    const dy = clientY - lastMouse.current.y;
    dragVel.current = {
      x: dragVel.current.x * 0.6 + dx * 0.4,
      y: dragVel.current.y * 0.6 + dy * 0.4,
    };
    lastMouse.current = { x: clientX, y: clientY };

    /* pendulum sway — body trails opposite to drag direction */
    const targetSway = Math.max(-30, Math.min(30, dragVel.current.x * 3));
    swayAngle.current = swayAngle.current * 0.72 + targetSway * 0.28;

    applyPos(
      anchor.current.px + (clientX - anchor.current.mx),
      anchor.current.py + (clientY - anchor.current.my),
    );

    const speed = Math.hypot(dx, dy);
    if (speed > 1.5) {
      applyStretch(dx, dy);
    } else {
      setScaleX(1.2); setScaleY(0.85);
    }

    if (Math.abs(dx) > 1.5) {
      const d: FaceDir = dx > 0 ? 'right' : 'left';
      if (d !== face.current) {
        face.current = d;
        setSprite(d === 'right' ? squishRight : squishLeft);
      }
    }
  }, [applyPos, applyStretch]);  // eslint-disable-line react-hooks/exhaustive-deps

  const endDrag = useCallback(() => {
    dragging.current = false;
    setIsDragging(false);
    resetScale();

    const gY = window.innerHeight - TASKBAR - PET_H;
    if (pos.current.y < gY - 10) {
      /* airborne throw — carry both axes of drag velocity */
      mode.current = 'airborne';
      vx.current   = dragVel.current.x * 0.65;
      vy.current   = Math.max(-18, Math.min(dragVel.current.y * 0.65, 12));
      if (dragVel.current.y < -2) { setScaleX(0.75); setScaleY(1.4); setTimeout(resetScale, 160); }
    } else {
      /* placed on ground — bleed horizontal into a short skid */
      mode.current  = 'idle';
      face.current  = 'front';
      vx.current    = dragVel.current.x * 0.3;
      swayAngle.current = swayAngle.current * 0.5;
    }

    setSprite(face.current === 'right' ? spriteRight
           : face.current === 'left'   ? spriteLeft : spriteFront);
    scheduleAction();
  }, [resetScale, scheduleAction]);  // eslint-disable-line react-hooks/exhaustive-deps

  /* ── mouse drag ── */
  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    startDrag(e.clientX, e.clientY);

    const onMove = (me: MouseEvent) => moveDrag(me.clientX, me.clientY);
    const onUp   = () => {
      endDrag();
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',   onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onUp);
  }, [startDrag, moveDrag, endDrag]);

  /* ── touch drag ── */
  const onTouchStart = useCallback((e: React.TouchEvent) => {
    e.stopPropagation();
    const t = e.touches[0];
    startDrag(t.clientX, t.clientY);

    const onMove = (te: TouchEvent) => {
      te.preventDefault();                       // stops page scroll while dragging pet
      const touch = te.touches[0];
      moveDrag(touch.clientX, touch.clientY);
    };
    const onUp = () => {
      endDrag();
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend',  onUp);
    };
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend',  onUp);
  }, [startDrag, moveDrag, endDrag]);

  /* ── render ── */
  return (
    <>
      <style>{`
        @keyframes petWalk {
          0%, 100% { transform: translateY(0px)  rotate(-2deg); }
          25%       { transform: translateY(-7px) rotate(0deg); }
          75%       { transform: translateY(-3px) rotate(2deg); }
        }
      `}</style>

      {/* Outer: position wrapper — only transform: translate written imperatively */}
      <div
        ref={el}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        style={{
          position:   'fixed',
          left:        0,
          top:         0,
          width:       PET_W,
          height:      PET_H,
          zIndex:      9995,   /* above windows + spotify widget, below taskbar (9999) */
          cursor:      isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
          willChange: 'transform',
        }}
      >
        {/* Squash / stretch layer (React state, CSS transition) */}
        <div
          style={{
            width:           '100%',
            height:          '100%',
            transform:       `scaleX(${scaleX}) scaleY(${scaleY})`,
            transition:      isDragging
              ? 'transform 0.06s ease-out'
              : 'transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1)',
            transformOrigin: 'bottom center',
          }}
        >
          {/* Walk bob layer (CSS animation, uses `transform` on a child
              so it doesn't fight the squash layer above) */}
          <div
            style={{
              width:           '100%',
              height:          '100%',
              transformOrigin: 'bottom center',
              animation:       isWalking ? 'petWalk 0.36s ease-in-out infinite' : 'none',
              position:        'relative',
              isolation:       'isolate',
            }}
          >
            <img
              src={sprite}
              alt="desktop pet"
              draggable={false}
              style={{
                width:          '100%',
                height:         '100%',
                objectFit:      'contain',
                imageRendering: 'pixelated',
                pointerEvents:  'none',
                display:        'block',
              }}
            />
            <div
              aria-hidden
              style={{
                position:        'absolute',
                inset:           0,
                backgroundColor: '#9a4210',
                mixBlendMode:    'multiply',
                opacity:         isDarkMode ? 0.52 : 0,
                transition:      'opacity 0.8s ease-in-out',
                WebkitMaskImage: `url("${sprite}")`,
                maskImage:       `url("${sprite}")`,
                WebkitMaskSize:  'contain',
                maskSize:        'contain',
                WebkitMaskRepeat:'no-repeat',
                maskRepeat:      'no-repeat',
                WebkitMaskPosition: 'center',
                maskPosition:    'center',
                pointerEvents:   'none',
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default DesktopPet;
